import React, { useCallback, useEffect } from 'react';
import { reduxForm } from 'redux-form';
import { Trans, useTranslation } from 'react-i18next';

import * as alert from '@common/services/alert';
import * as colorUtils from '@common/utils/color';
import { Button } from '@common/components/button';
import Icon from '@common/components/icon';
import Overview from '@common/components/overview';
import Confirm from '@common/components/confirm-button';
import ThemePreview from '@common/components/theme-preview';
import FilePreview from '@common/components/file-preview/image';
import { Row, Group, Label } from '@common/components/form';
import ColorInput from '@common/components/form/inputs/color';
import FileCropInput from '@common/components/form/inputs/file-crop';

import { useIsAvailableInPlanPackage } from '@common/hooks/use-is-available-in-plan-package';
import { useFormValues } from '@common/hooks';
import { EPlanPackageConfig } from '@common/definitions';
import { updateOrganisation } from '../../actions';

const INITIAL_VALUES = {
  theme_background_header: null,
  brand_icon: null,
  theme: {
    main_color: null,
    button_color: null,
    link_color: null,
  },
};

const getBackgroundUrl = ({ theme_background_header }, { header_background_url }) => {
  if (theme_background_header) return theme_background_header.preview;
  return theme_background_header === false ? null : header_background_url;
};

const OrganisationThemeForm = ({
  organisation, loggedUser, submitting, pristine, initialize, reset, handleSubmit, change,
}) => {
  const { t } = useTranslation();
  const formValues = useFormValues('organisation-theme');
  const {
    isAvailable: canConfigureTheming, showUpgradeModal,
  } = useIsAvailableInPlanPackage(EPlanPackageConfig.THEMING);

  useEffect(() => {
    initialize({
      theme_background_header: null,
      brand_icon: null,
      theme: {
        main_color: organisation.theme.main_color,
        button_color: organisation.theme.button_color,
        link_color: organisation.theme.link_color,
      },
    });
  }, []);

  const onSubmit = useCallback(async (values, dispatch) => {
    try {
      const result = await dispatch(updateOrganisation(values));
      await colorUtils.setTheme(result.organisation.theme);
      alert.success(t('organisation:forms_theme_success'));
    } catch (response) {
      alert.forStatus(response.status_code, {
        warning: t('organisation:forms_theme_warning'),
        error: t('organisation:forms_theme_error'),
      });
    }
  }, [t]);

  const onReset = useCallback(async (_, dispatch) => {
    await onSubmit(INITIAL_VALUES, dispatch);
    reset();
  }, [onSubmit, reset]);

  const onClearHeaderImage = useCallback((e) => {
    e.stopPropagation();
    change('theme_background_header', false);
  }, [change]);

  const backgroundUrl = getBackgroundUrl(formValues, organisation.theme);

  return (
    <>
      <Overview>
        <Overview.Header>
          <Overview.Header.Title><Trans i18nKey="organisation:forms_theme_title" /></Overview.Header.Title>
        </Overview.Header>
        <Overview.Content>
          <form
            className="Form Form--horizontal"
            onSubmit={
              canConfigureTheming ?
                handleSubmit(onSubmit) :
                (e) => {
                  e.preventDefault();
                  showUpgradeModal();
                }
            }
          >
            <Row>
              <Group>
                <Label text={t('organisation:forms_organisation_logo')} />
                <Row>
                  {formValues.brand_icon ? (
                    <div className="Form__Row__Preview">
                      <FilePreview size={100} file={formValues.brand_icon} />
                    </div>
                  ) : organisation.brand_icon && (
                    <div className="Form__Row__Preview" style={{ width: 100, height: 100 }}>
                      <img src={organisation.brand_icon} alt={organisation.name} />
                    </div>
                  )}
                  <div>
                    <FileCropInput name="brand_icon">
                      <Button>
                        {
                          formValues.brandIcon ?
                            t('organisation:forms_organisation_logo_change') :
                            t('organisation:forms_organisation_logo_new')
                        }
                      </Button>
                    </FileCropInput>
                  </div>
                </Row>
              </Group>
            </Row>
            <Row>
              <Group>
                <Label text={t('organisation:forms_theme_main_color')} />
                <ColorInput name="theme.main_color" defaultColor="#19214D" />
                <br />
                <Label text={t('organisation:forms_theme_button_color')} />
                <ColorInput name="theme.button_color" defaultColor="#007AFF" />
                <br />
                <Label text={t('organisation:forms_theme_link_color')} />
                <ColorInput name="theme.link_color" defaultColor="#007AFF" />
              </Group>
              <Group flex={3}>
                <Label text={t('organisation:forms_theme_header_image')} />
                <FileCropInput name="theme_background_header" aspectRatio={2.3399014778}>
                  {backgroundUrl ? (
                    <div className="ThemeForm__HeaderImage">
                      <div className="ThemeForm__HeaderImage__Remove" onClick={onClearHeaderImage}>
                        <Icon type="close" />
                      </div>
                      <img
                        src={backgroundUrl}
                        alt={t('organisation:forms_theme_header_image')}
                        style={{ maxHeight: 180 }}
                        className="ThemeForm__HeaderImage__Image"
                      />
                    </div>
                  ) : (
                    <Button size="large">
                      <Trans i18nKey="organisation:forms_theme_upload_header_image" />
                    </Button>
                  )}
                </FileCropInput>
              </Group>
            </Row>
            <Row>
              <Button buttonType="submit" type="primary" disabled={pristine} isLoading={submitting}>
                <Trans i18nKey="organisation:forms_theme_submit" />
              </Button>
              <Confirm
                title={t('organisation:forms_theme_reset_confirm_title')}
                onConfirm={handleSubmit(onReset)}
              >
                <Button isLoading={submitting}>
                  <Trans i18nKey="organisation:forms_theme_reset_confirm_button" />
                </Button>
              </Confirm>
            </Row>
          </form>
        </Overview.Content>
      </Overview>
      <Overview>
        <div className="ThemePreview__Preview">
          <Overview.Header>
            <Overview.Header.Title>
              <Trans i18nKey="organisation:forms_theme_example_title" />
            </Overview.Header.Title>
          </Overview.Header>
          <Overview.Content>
            {formValues && formValues.theme && (
              <style>
                {`*[fill="primaryColor"] {
                  fill: ${formValues.theme.main_color || '#19214D'};
                }

                *[fill="buttonColor"] {
                  fill: ${formValues.theme.button_color || '#007AFF'};
                }`}
              </style>
            )}
            <ThemePreview loggedUser={loggedUser} organisation={organisation} />
          </Overview.Content>
        </div>
      </Overview>
    </>
  );
};

export default reduxForm({
  form: 'organisation-theme',
  initialValues: INITIAL_VALUES,
})(OrganisationThemeForm);
